import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { buildMonthlyAverages } from "./monthlyAverages";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

// RPM list thumbnail: month-by-month progression, one point per month = that
// month's average (monthlyAverages.js). Colours follow the patient's RPM chart
// (graphs/linegraphs/trendslinegraph.js): systolic orange, diastolic navy.
const ORANGE = "#FB923C";
const NAVY = "#1D2951";

const SERIES = {
  bp: [
    { key: "systolic", label: "Systolic", color: ORANGE },
    { key: "diastolic", label: "Diastolic", color: NAVY },
  ],
  weight: [{ key: "weight", label: "Weight", color: ORANGE }],
};

const mkStyle = (color) => ({
  fill: false,
  tension: 0,
  borderColor: color,
  backgroundColor: color,
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBorderWidth: 2,
  pointBackgroundColor: color,
  pointBorderColor: "#ffffff",
  borderWidth: 2,
  spanGaps: true,
});

// Exported for tests: the chart.js `data` + the month rows behind it.
export function buildInfographData(readings, weightOnly = false) {
  const series = weightOnly ? SERIES.weight : SERIES.bp;
  const months = buildMonthlyAverages(readings, series.map((s) => s.key));
  return {
    months,
    data: {
      labels: months.map((m) => m.label),
      datasets: series.map((s) => ({
        ...mkStyle(s.color),
        label: s.label,
        data: months.map((m) => m.values[s.key]),
      })),
    },
  };
}

function Infograph({ data = [], weightOnly = false }) {
  const { months, data: chartData } = useMemo(
    () => buildInfographData(data, weightOnly),
    [data, weightOnly]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      layout: { padding: { left: 6, right: 12, top: 6 } },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: { usePointStyle: true, boxWidth: 6, font: { size: 10 } },
        },
        tooltip: {
          enabled: true,
          backgroundColor: "#ffffff",
          titleColor: "#111827",
          bodyColor: "#111827",
          footerColor: "#6b7280",
          borderColor: "#E5E7EB",
          borderWidth: 1,
          padding: 10,
          // A month with weight but no BP (or the reverse) has a null point.
          filter: (item) => item.raw !== null && item.raw !== undefined,
          callbacks: {
            title: (items) => months[items[0]?.dataIndex]?.title ?? "",
            label: (ctx) => `${ctx.dataset.label} avg: ${ctx.raw}${weightOnly ? " lbs" : ""}`,
            footer: (items) => {
              const n = months[items[0]?.dataIndex]?.count || 0;
              return `${n} reading${n === 1 ? "" : "s"}`;
            },
          },
        },
      },
      scales: {
        x: {
          offset: true,
          grid: { display: false },
          ticks: { maxRotation: 0, autoSkip: false, font: { size: 10 } },
        },
        y: {
          grace: "10%",
          grid: { drawTicks: false, color: "#E5E7EB" },
          ticks: { font: { size: 10 } },
        },
      },
    }),
    [months, weightOnly]
  );

  if (!months.length) {
    return (
      <div
        className="flex justify-center items-center rounded-lg"
        style={{ height: 260, width: 360 }}
      >
        <span className="text-xs text-[#9CA3AF]">No readings</span>
      </div>
    );
  }

  return (
    <div style={{ height: 260, width: 360, position: "relative" }}>
      <Line data={chartData} options={options} />
    </div>
  );
}

export default Infograph;
